import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { getErrorMessage } from '../utils/errorUtils';
import { 
  Shield,
  Building2,
  Users,
  Trophy,
  DollarSign,
  Search,
  Edit,
  Trash2,
  Crown,
  ChevronRight,
  LogOut,
  ArrowLeft,
  CheckCircle,
  XCircle,
  BarChart3,
  TrendingUp,
  Activity,
  Zap,
  MousePointer,
  Calendar,
  Video,
  Swords,
  Scale,
  UserCheck,
  ClipboardList,
  KeyRound,
  Copy,
  Clock,
  Dumbbell
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { cn } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Super Admin Layout
const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#09090b]">
      <header className="bg-zinc-950 border-b border-zinc-800 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-purple-600 rounded-sm flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-white text-xl uppercase tracking-tight">
                Platform Admin
              </h1>
              <p className="text-xs text-zinc-500">Black Arrow MMA</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-zinc-400 text-sm">{user?.email}</span>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-zinc-400 hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto p-6">
        {children}
      </main>
    </div>
  );
};

// Stats Card
const StatCard = ({ title, value, icon: Icon, color = "purple" }) => (
  <div className="bg-zinc-900/50 border border-zinc-800 p-6 rounded-sm">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-zinc-400 text-sm mb-1">{title}</p>
        <p className="font-heading text-3xl font-bold text-white tabular-nums">{value}</p>
      </div>
      <div className={`w-12 h-12 bg-${color}-600/20 rounded-sm flex items-center justify-center`}>
        <Icon className={`w-6 h-6 text-${color}-500`} />
      </div>
    </div>
  </div>
);

// Main Admin Panel
export const AdminPanelPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [gyms, setGyms] = useState([]);
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchGyms, setSearchGyms] = useState('');
  const [searchUsers, setSearchUsers] = useState('');
  
  // Edit subscription dialog
  const [editGym, setEditGym] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState({ tier: 'none', status: 'inactive' });
  const [saving, setSaving] = useState(false);
  
  // Delete dialogs
  const [deleteGymId, setDeleteGymId] = useState(null);
  const [deleteUserId, setDeleteUserId] = useState(null);
  
  // Edit user dialog
  const [editUser, setEditUser] = useState(null);
  const [editUserData, setEditUserData] = useState({ name: '', email: '' });
  const [savingUser, setSavingUser] = useState(false);
  
  // Reset password dialog
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [tempPassword, setTempPassword] = useState(null);
  const [resettingPassword, setResettingPassword] = useState(false);

  useEffect(() => {
    if (user?.role !== 'superadmin') {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      const [statsRes, gymsRes, usersRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/gyms`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/analytics/feature-usage`)
      ]);
      setStats(statsRes.data);
      setGyms(gymsRes.data);
      setUsers(usersRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubscription = (gym) => {
    setEditGym(gym);
    // Use sport_type if available, otherwise fall back to subscription_tier
    const tier = gym.sport_type || gym.subscription_tier || 'none';
    setSubscriptionData({
      tier: tier,
      status: gym.subscription_status || 'inactive'
    });
  };

  const handleSaveSubscription = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/gyms/${editGym.id}/subscription`, {
        subscription_tier: subscriptionData.tier,
        subscription_status: subscriptionData.status
      });
      toast.success('Subscription updated');
      setEditGym(null);
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update subscription'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGym = async () => {
    try {
      await axios.delete(`${API}/admin/gyms/${deleteGymId}`);
      toast.success('Gym deleted');
      setDeleteGymId(null);
      loadData();
    } catch (error) {
      toast.error('Failed to delete gym');
    }
  };

  const handleDeleteUser = async () => {
    try {
      await axios.delete(`${API}/admin/users/${deleteUserId}`);
      toast.success('User deleted');
      setDeleteUserId(null);
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete user'));
    }
  };

  const handleEditUser = (u) => {
    setEditUser(u);
    setEditUserData({ name: u.name, email: u.email });
  };

  const handleSaveUser = async () => {
    setSavingUser(true);
    try {
      await axios.put(`${API}/admin/users/${editUser.id}/update`, editUserData);
      toast.success('User updated successfully');
      setEditUser(null);
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update user'));
    } finally {
      setSavingUser(false);
    }
  };

  const handleResetPassword = async () => {
    setResettingPassword(true);
    try {
      const response = await axios.put(`${API}/admin/users/${resetPasswordUser.id}/reset-password`);
      setTempPassword(response.data.temporary_password);
      toast.success('Password reset successfully');
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to reset password'));
      setResetPasswordUser(null);
    } finally {
      setResettingPassword(false);
    }
  };

  const filteredGyms = gyms.filter(gym => 
    gym.name.toLowerCase().includes(searchGyms.toLowerCase()) ||
    gym.email.toLowerCase().includes(searchGyms.toLowerCase())
  );

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchUsers.toLowerCase()) ||
    user.email.toLowerCase().includes(searchUsers.toLowerCase())
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-panel">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="Total Gyms" value={stats?.total_gyms || 0} icon={Building2} />
          <StatCard title="Total Users" value={stats?.total_users || 0} icon={Users} />
          <StatCard title="Active Subscriptions" value={stats?.subscriptions?.total_active || 0} icon={Crown} color="yellow" />
          <StatCard title="Monthly Revenue" value={`$${stats?.monthly_revenue || 0}`} icon={DollarSign} color="green" />
        </div>

        {/* Subscription Breakdown - Updated to new sport types */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-zinc-900/50 border border-blue-600/30 p-4 rounded-sm">
            <div className="flex items-center justify-between">
              <span className="text-zinc-400">BJJ Plans</span>
              <span className="text-blue-500 font-bold">{stats?.subscriptions?.bjj || stats?.subscriptions?.silver || 0}</span>
            </div>
            <p className="text-xs text-zinc-500 mt-1">$299.99/mo</p>
          </div>
          <div className="bg-zinc-900/50 border border-red-600/30 p-4 rounded-sm">
            <div className="flex items-center justify-between">
              <span className="text-zinc-400">MMA Plans</span>
              <span className="text-red-500 font-bold">{stats?.subscriptions?.mma || stats?.subscriptions?.gold || 0}</span>
            </div>
            <p className="text-xs text-zinc-500 mt-1">$399.99/mo</p>
          </div>
          <div className="bg-zinc-900/50 border border-yellow-600/30 p-4 rounded-sm">
            <div className="flex items-center justify-between">
              <span className="text-zinc-400">Hybrid Plans</span>
              <span className="text-yellow-500 font-bold">{stats?.subscriptions?.hybrid || 0}</span>
            </div>
            <p className="text-xs text-zinc-500 mt-1">$499.99/mo</p>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 p-4 rounded-sm">
            <div className="flex items-center justify-between">
              <span className="text-zinc-400">Total Fighters</span>
              <span className="text-white font-bold">{stats?.total_fighters || 0}</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="analytics" className="space-y-6">
          <TabsList className="bg-zinc-900 border border-zinc-800 p-1">
            <TabsTrigger 
              value="analytics" 
              className="data-[state=active]:bg-purple-600 data-[state=active]:text-white"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger 
              value="gyms" 
              className="data-[state=active]:bg-purple-600 data-[state=active]:text-white"
            >
              <Building2 className="w-4 h-4 mr-2" />
              Gyms ({gyms.length})
            </TabsTrigger>
            <TabsTrigger 
              value="users"
              className="data-[state=active]:bg-purple-600 data-[state=active]:text-white"
            >
              <Users className="w-4 h-4 mr-2" />
              Users ({users.length})
            </TabsTrigger>
          </TabsList>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <div className="space-y-6">
              {/* Feature Usage Overview */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Most Used Features */}
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
                  <div className="flex items-center gap-2 mb-6">
                    <TrendingUp className="w-5 h-5 text-purple-500" />
                    <h3 className="text-lg font-heading font-bold text-white uppercase">Feature Usage (Last 7 Days)</h3>
                  </div>
                  
                  {analytics?.by_feature?.length > 0 ? (
                    <div className="space-y-4">
                      {analytics.by_feature.map((feature, idx) => {
                        const maxCount = analytics.by_feature[0]?.count || 1;
                        const percentage = (feature.count / maxCount) * 100;
                        const featureIcons = {
                          'fighters': Users,
                          'training_camps': Calendar,
                          'assessments': ClipboardList,
                          'fights': Swords,
                          'videos': Video,
                          'weight_tracking': Scale,
                          'attendance': UserCheck,
                          'dashboard': Activity,
                          'settings': Shield
                        };
                        const FeatureIcon = featureIcons[feature.feature] || Zap;
                        
                        return (
                          <div key={idx} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <FeatureIcon className="w-4 h-4 text-zinc-400" />
                                <span className="text-zinc-300 capitalize">
                                  {feature.feature.replace(/_/g, ' ')}
                                </span>
                              </div>
                              <span className="text-purple-400 font-bold tabular-nums">{feature.count}</span>
                            </div>
                            <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                              <motion.div 
                                initial={{ width: 0 }}
                                animate={{ width: `${percentage}%` }}
                                transition={{ duration: 0.5, delay: idx * 0.1 }}
                                className="h-full bg-gradient-to-r from-purple-600 to-purple-400 rounded-full"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Activity className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                      <p className="text-zinc-500">No usage data yet</p>
                      <p className="text-zinc-600 text-sm mt-1">Feature usage will appear here as users interact with the platform</p>
                    </div>
                  )}
                </div>

                {/* Most Active Gyms */}
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
                  <div className="flex items-center gap-2 mb-6">
                    <Building2 className="w-5 h-5 text-green-500" />
                    <h3 className="text-lg font-heading font-bold text-white uppercase">Most Active Gyms</h3>
                  </div>
                  
                  {analytics?.by_gym?.length > 0 ? (
                    <div className="space-y-3">
                      {analytics.by_gym.slice(0, 10).map((gym, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-sm"
                        >
                          <div className="flex items-center gap-3">
                            <span className={cn(
                              "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold",
                              idx === 0 ? "bg-yellow-500 text-yellow-900" :
                              idx === 1 ? "bg-zinc-400 text-zinc-900" :
                              idx === 2 ? "bg-amber-600 text-amber-100" :
                              "bg-zinc-700 text-zinc-300"
                            )}>
                              {idx + 1}
                            </span>
                            <span className="text-zinc-300">{gym.gym_name}</span>
                          </div>
                          <span className="text-green-400 font-bold tabular-nums">{gym.count} actions</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Building2 className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                      <p className="text-zinc-500">No gym activity yet</p>
                      <p className="text-zinc-600 text-sm mt-1">Activity will appear as gyms use the platform</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Daily Usage Trend */}
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
                <div className="flex items-center gap-2 mb-6">
                  <MousePointer className="w-5 h-5 text-blue-500" />
                  <h3 className="text-lg font-heading font-bold text-white uppercase">Daily Usage Trend</h3>
                </div>
                
                {analytics?.daily_trend?.length > 0 ? (
                  <div className="space-y-4">
                    <div className="flex items-end gap-2 h-40">
                      {analytics.daily_trend.map((day, idx) => {
                        const maxCount = Math.max(...analytics.daily_trend.map(d => d.count)) || 1;
                        const height = (day.count / maxCount) * 100;
                        
                        return (
                          <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                            <motion.div 
                              initial={{ height: 0 }}
                              animate={{ height: `${height}%` }}
                              transition={{ duration: 0.5, delay: idx * 0.05 }}
                              className="w-full bg-gradient-to-t from-blue-600 to-blue-400 rounded-t-sm min-h-[4px]"
                            />
                            <span className="text-zinc-500 text-xs">
                              {format(parseISO(day.date), 'M/d')}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex justify-between text-sm text-zinc-500 pt-4 border-t border-zinc-800">
                      <span>Total Actions: {analytics.daily_trend.reduce((a, b) => a + b.count, 0)}</span>
                      <span>Avg/Day: {Math.round(analytics.daily_trend.reduce((a, b) => a + b.count, 0) / analytics.daily_trend.length)}</span>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <BarChart3 className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                    <p className="text-zinc-500">No trend data available</p>
                    <p className="text-zinc-600 text-sm mt-1">Usage trends will appear after a few days of activity</p>
                  </div>
                )}
              </div>

              {/* Feature Insights */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4">
                  <div className="flex items-center gap-2 text-zinc-400 mb-2">
                    <Zap className="w-4 h-4" />
                    <span className="text-sm">Most Popular Feature</span>
                  </div>
                  <p className="text-xl font-bold text-white capitalize">
                    {analytics?.by_feature?.[0]?.feature?.replace(/_/g, ' ') || 'N/A'}
                  </p>
                </div>
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4">
                  <div className="flex items-center gap-2 text-zinc-400 mb-2">
                    <Building2 className="w-4 h-4" />
                    <span className="text-sm">Most Active Gym</span>
                  </div>
                  <p className="text-xl font-bold text-white">
                    {analytics?.by_gym?.[0]?.gym_name || 'N/A'}
                  </p>
                </div>
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4">
                  <div className="flex items-center gap-2 text-zinc-400 mb-2">
                    <Activity className="w-4 h-4" />
                    <span className="text-sm">Total Actions (7 Days)</span>
                  </div>
                  <p className="text-xl font-bold text-white tabular-nums">
                    {analytics?.by_feature?.reduce((a, b) => a + b.count, 0) || 0}
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Gyms Tab */}
          <TabsContent value="gyms">
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  placeholder="Search gyms..."
                  value={searchGyms}
                  onChange={(e) => setSearchGyms(e.target.value)}
                  className="pl-10 h-12 bg-zinc-900 border-zinc-800"
                  data-testid="search-gyms-input"
                />
              </div>

              <div className="space-y-3">
                {filteredGyms.map((gym) => {
                  const tierDisplay = gym.sport_type || gym.subscription_tier;
                  const tierColorClass = 
                    tierDisplay === 'hybrid' ? "bg-yellow-500/20 text-yellow-500 border-yellow-500/30" :
                    tierDisplay === 'mma' || tierDisplay === 'gold' ? "bg-red-500/20 text-red-500 border-red-500/30" :
                    tierDisplay === 'bjj' || tierDisplay === 'silver' ? "bg-blue-500/20 text-blue-500 border-blue-500/30" :
                    "bg-zinc-600/20 text-zinc-400 border-zinc-600/30";
                  
                  return (
                  <motion.div
                    key={gym.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-sm hover:border-zinc-700 transition-colors"
                    data-testid={`gym-row-${gym.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-zinc-800 rounded-sm flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-zinc-400" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{gym.name}</p>
                          <p className="text-zinc-500 text-sm">{gym.email}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <div className="flex items-center gap-2">
                            {tierDisplay && tierDisplay !== 'none' && (
                              <span className={cn(
                                "px-2 py-0.5 text-xs uppercase font-medium rounded-sm border",
                                tierColorClass
                              )}>
                                {tierDisplay}
                              </span>
                            )}
                            {gym.subscription_status === 'active' ? (
                              <CheckCircle className="w-4 h-4 text-green-500" />
                            ) : gym.subscription_status === 'trial' ? (
                              <Clock className="w-4 h-4 text-yellow-500" />
                            ) : (
                              <XCircle className="w-4 h-4 text-zinc-500" />
                            )}
                          </div>
                          <p className="text-zinc-500 text-xs mt-1">
                            {gym.users_count} users • {gym.fighters_count} fighters
                          </p>
                        </div>
                        
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditSubscription(gym)}
                            className="text-zinc-400 hover:text-purple-500"
                            data-testid={`edit-gym-${gym.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDeleteGymId(gym.id)}
                            className="text-zinc-400 hover:text-red-500"
                            data-testid={`delete-gym-${gym.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                  );
                })}

                {filteredGyms.length === 0 && (
                  <div className="text-center py-12 text-zinc-500">
                    No gyms found
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  placeholder="Search users..."
                  value={searchUsers}
                  onChange={(e) => setSearchUsers(e.target.value)}
                  className="pl-10 h-12 bg-zinc-900 border-zinc-800"
                  data-testid="search-users-input"
                />
              </div>

              <div className="space-y-3">
                {filteredUsers.map((u) => (
                  <motion.div
                    key={u.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-sm hover:border-zinc-700 transition-colors"
                    data-testid={`user-row-${u.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={cn(
                          "w-10 h-10 rounded-sm flex items-center justify-center",
                          u.role === 'superadmin' ? "bg-purple-600/20" : 
                          u.role === 'admin' ? "bg-red-600/20" : "bg-zinc-800"
                        )}>
                          {u.role === 'superadmin' ? (
                            <Shield className="w-5 h-5 text-purple-500" />
                          ) : (
                            <Users className="w-5 h-5 text-zinc-400" />
                          )}
                        </div>
                        <div>
                          <p className="text-white font-medium">{u.name}</p>
                          <p className="text-zinc-500 text-sm">{u.email}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <span className={cn(
                            "px-2 py-0.5 text-xs uppercase font-medium rounded-sm",
                            u.role === 'superadmin' ? "bg-purple-600/20 text-purple-500" :
                            u.role === 'admin' ? "bg-red-600/20 text-red-500" : "bg-zinc-700/20 text-zinc-400"
                          )}>
                            {u.role}
                          </span>
                          <p className="text-zinc-500 text-xs mt-1">{u.gym_name}</p>
                        </div>
                        
                        {u.role !== 'superadmin' && (
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEditUser(u)}
                              className="text-zinc-400 hover:text-blue-500"
                              data-testid={`edit-user-${u.id}`}
                              title="Edit User"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setResetPasswordUser(u)}
                              className="text-zinc-400 hover:text-yellow-500"
                              data-testid={`reset-password-${u.id}`}
                              title="Reset Password"
                            >
                              <KeyRound className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setDeleteUserId(u.id)}
                              className="text-zinc-400 hover:text-red-500"
                              data-testid={`delete-user-${u.id}`}
                              title="Delete User"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}

                {filteredUsers.length === 0 && (
                  <div className="text-center py-12 text-zinc-500">
                    No users found
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Edit Subscription Dialog */}
        <Dialog open={!!editGym} onOpenChange={() => setEditGym(null)}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-white font-heading uppercase">
                Edit Subscription - {editGym?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Sport Type / Subscription Tier</Label>
                <Select 
                  value={subscriptionData.tier} 
                  onValueChange={(value) => setSubscriptionData({...subscriptionData, tier: value})}
                >
                  <SelectTrigger className="h-12 bg-zinc-950 border-zinc-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="bjj">BJJ ($299.99/mo)</SelectItem>
                    <SelectItem value="mma">MMA ($399.99/mo)</SelectItem>
                    <SelectItem value="hybrid">Hybrid ($499.99/mo)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Status</Label>
                <Select 
                  value={subscriptionData.status} 
                  onValueChange={(value) => setSubscriptionData({...subscriptionData, status: value})}
                >
                  <SelectTrigger className="h-12 bg-zinc-950 border-zinc-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="trial">Trial</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditGym(null)} className="border-zinc-700">
                Cancel
              </Button>
              <Button 
                onClick={handleSaveSubscription}
                disabled={saving}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="save-subscription-btn"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Gym Confirmation */}
        <AlertDialog open={!!deleteGymId} onOpenChange={() => setDeleteGymId(null)}>
          <AlertDialogContent className="bg-zinc-900 border-zinc-800">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Delete Gym</AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                Are you sure? This will permanently delete the gym and ALL its data including users, fighters, camps, and fights.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-300">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteGym}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Delete Everything
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete User Confirmation */}
        <AlertDialog open={!!deleteUserId} onOpenChange={() => setDeleteUserId(null)}>
          <AlertDialogContent className="bg-zinc-900 border-zinc-800">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Delete User</AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                Are you sure you want to delete this user?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-300">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteUser}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Delete User
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Edit User Dialog */}
        <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-white font-heading uppercase">
                Edit User
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Name</Label>
                <Input
                  value={editUserData.name}
                  onChange={(e) => setEditUserData({...editUserData, name: e.target.value})}
                  className="h-12 bg-zinc-950 border-zinc-800"
                  placeholder="User name"
                  data-testid="edit-user-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Email</Label>
                <Input
                  type="email"
                  value={editUserData.email}
                  onChange={(e) => setEditUserData({...editUserData, email: e.target.value})}
                  className="h-12 bg-zinc-950 border-zinc-800"
                  placeholder="user@email.com"
                  data-testid="edit-user-email-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditUser(null)} className="border-zinc-700">
                Cancel
              </Button>
              <Button 
                onClick={handleSaveUser}
                disabled={savingUser}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="save-user-btn"
              >
                {savingUser ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={!!resetPasswordUser} onOpenChange={() => { setResetPasswordUser(null); setTempPassword(null); }}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-white font-heading uppercase flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-yellow-500" />
                Reset Password
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              {!tempPassword ? (
                <div className="space-y-4">
                  <p className="text-zinc-400">
                    Reset password for <span className="text-white font-medium">{resetPasswordUser?.name}</span> ({resetPasswordUser?.email})?
                  </p>
                  <p className="text-zinc-500 text-sm">
                    This will generate a temporary password that the user must change after logging in.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-green-600/10 border border-green-600/30 rounded-sm p-4">
                    <p className="text-green-500 text-sm font-medium mb-2">Password Reset Successful!</p>
                    <p className="text-zinc-400 text-sm mb-3">Temporary password for {resetPasswordUser?.email}:</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-zinc-950 border border-zinc-800 px-4 py-3 rounded text-yellow-500 font-mono text-lg">
                        {tempPassword}
                      </code>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          navigator.clipboard.writeText(tempPassword);
                          toast.success('Password copied to clipboard');
                        }}
                        className="border-zinc-700"
                        data-testid="copy-password-btn"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-zinc-500 text-sm">
                    Please securely share this password with the user. They will need to change it after logging in.
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              {!tempPassword ? (
                <>
                  <Button variant="outline" onClick={() => setResetPasswordUser(null)} className="border-zinc-700">
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleResetPassword}
                    disabled={resettingPassword}
                    className="bg-yellow-600 hover:bg-yellow-700"
                    data-testid="confirm-reset-password-btn"
                  >
                    {resettingPassword ? 'Resetting...' : 'Reset Password'}
                  </Button>
                </>
              ) : (
                <Button 
                  onClick={() => { setResetPasswordUser(null); setTempPassword(null); }}
                  className="bg-zinc-700 hover:bg-zinc-600"
                >
                  Done
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

// Super Admin Login/Setup Page
export const AdminSetupPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [mode, setMode] = useState('login'); // 'login' or 'setup'
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    secret_key: ''
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(formData.email, formData.password);
      if (result.user.role === 'superadmin') {
        toast.success('Welcome, Super Admin!');
        navigate('/admin');
      } else {
        toast.error('This account is not a super admin');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/setup`, formData);
      localStorage.setItem('token', response.data.access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
      toast.success('Super Admin account created!');
      window.location.href = '/admin';
    } catch (error) {
      toast.error(getErrorMessage(error, 'Setup failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-purple-600 rounded-sm flex items-center justify-center mx-auto mb-4">
            <Shield className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-white uppercase tracking-tight">
            Platform Admin
          </h1>
          <p className="text-zinc-400 mt-2">Black Arrow MMA Management</p>
        </div>

        <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
          <div className="flex gap-2 mb-6">
            <Button
              variant={mode === 'login' ? 'default' : 'ghost'}
              onClick={() => setMode('login')}
              className={mode === 'login' ? 'bg-purple-600 flex-1' : 'flex-1 text-zinc-400'}
            >
              Login
            </Button>
            <Button
              variant={mode === 'setup' ? 'default' : 'ghost'}
              onClick={() => setMode('setup')}
              className={mode === 'setup' ? 'bg-purple-600 flex-1' : 'flex-1 text-zinc-400'}
            >
              Setup
            </Button>
          </div>

          <form onSubmit={mode === 'login' ? handleLogin : handleSetup} className="space-y-4">
            {mode === 'setup' && (
              <div className="space-y-2">
                <Label className="text-zinc-300">Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Admin Name"
                  className="h-12 bg-zinc-950 border-zinc-800"
                  required
                  data-testid="admin-name-input"
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label className="text-zinc-300">Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="admin@blackarrow.com"
                className="h-12 bg-zinc-950 border-zinc-800"
                required
                data-testid="admin-email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-300">Password</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                placeholder="••••••••"
                className="h-12 bg-zinc-950 border-zinc-800"
                required
                data-testid="admin-password-input"
              />
            </div>

            {mode === 'setup' && (
              <div className="space-y-2">
                <Label className="text-zinc-300">Secret Key</Label>
                <Input
                  type="password"
                  value={formData.secret_key}
                  onChange={(e) => setFormData({...formData, secret_key: e.target.value})}
                  placeholder="Platform secret key"
                  className="h-12 bg-zinc-950 border-zinc-800"
                  required
                  data-testid="admin-secret-input"
                />
                <p className="text-zinc-500 text-xs">Required to create the super admin account</p>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-purple-600 hover:bg-purple-700 text-white uppercase font-heading font-bold"
              data-testid="admin-submit-btn"
            >
              {loading ? 'Please wait...' : mode === 'login' ? 'Login' : 'Create Super Admin'}
            </Button>
          </form>
        </div>

        <p className="text-center text-zinc-500 text-sm mt-6">
          <a href="/" className="text-purple-500 hover:text-purple-400">
            ← Back to main site
          </a>
        </p>
      </motion.div>
    </div>
  );
};

export default AdminPanelPage;
